import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTourContext } from './TourProvider';

interface TourTooltipProps {
  targetRect: {
    top: number;
    left: number;
    width: number;
    height: number;
    right: number;
    bottom: number;
  };
}

export function TourTooltip({ targetRect }: TourTooltipProps) {
  const {
    currentStep,
    currentStepData,
    totalSteps,
    nextStep,
    prevStep,
    closeTour,
    finishTour,
    dismissGuide,
  } = useTourContext();
  
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  // Focus trap
  useEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    const focusableElements = tooltip.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      } else if (e.key === 'Escape') {
        closeTour();
      } else if (e.key === 'ArrowRight' && !isLastStep) {
        nextStep();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeTour, nextStep, prevStep, isFirstStep, isLastStep]);

  if (!currentStepData) return null;

  const placement = currentStepData.placement || 'right';
  const isBottom = placement === 'bottom';
  const tooltipWidth = 288; // w-72 = 18rem = 288px
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const gap = 12;

  // Position tooltip based on placement
  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10000,
  };

  if (isBottom) {
    tooltipStyle.top = targetRect.bottom + gap;
    // Center on target, but clamp within viewport
    let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    left = Math.max(8, Math.min(left, viewportWidth - tooltipWidth - 8));
    tooltipStyle.left = left;

    // If tooltip would overflow bottom, place above instead
    if (targetRect.bottom + gap + 200 > viewportHeight) {
      tooltipStyle.top = undefined;
      tooltipStyle.bottom = viewportHeight - targetRect.top + gap;
    }
  } else {
    tooltipStyle.top = targetRect.top + targetRect.height / 2;
    tooltipStyle.left = targetRect.right + 16;
    tooltipStyle.transform = 'translateY(-50%)';

    // Check if tooltip would overflow right edge
    if (targetRect.right + tooltipWidth + 16 > viewportWidth) {
      tooltipStyle.left = targetRect.left - 16;
      tooltipStyle.transform = 'translateX(-100%) translateY(-50%)';
    }
  }

  return (
    <motion.div
      ref={tooltipRef}
      initial={{ opacity: 0, scale: 0.95, x: -10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.95, x: -10 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      style={tooltipStyle}
      className="w-72 bg-card border border-border rounded-xl shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      aria-describedby="tour-description"
    >
      {/* Arrow */}
      {isBottom ? (() => {
        // Compute arrow position relative to tooltip left edge
        const tooltipLeft = typeof tooltipStyle.left === 'number' ? tooltipStyle.left : 0;
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const arrowOffset = Math.max(16, Math.min(targetCenterX - tooltipLeft, tooltipWidth - 16));
        return (
          <>
            <div 
              className="absolute top-0 -translate-y-full"
              style={{ left: arrowOffset, width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid hsl(var(--border))', transform: 'translateX(-50%) translateY(0)' }}
            />
            <div 
              className="absolute top-0"
              style={{ left: arrowOffset, width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: '7px solid hsl(var(--card))', marginTop: '-7px', transform: 'translateX(-50%)' }}
            />
          </>
        );
      })() : (
        <>
          <div 
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full"
            style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderRight: '8px solid hsl(var(--border))' }}
          />
          <div 
            className="absolute left-0 top-1/2 -translate-y-1/2"
            style={{ width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderRight: '7px solid hsl(var(--card))', marginLeft: '-7px' }}
          />
        </>
      )}

      {/* Close button */}
      <button
        onClick={closeTour}
        className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        aria-label="Close tour"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Content */}
      <div className="p-4 pt-6">
        <h3 
          id="tour-title"
          className="text-lg font-semibold text-foreground mb-2"
        >
          {currentStepData.title}
        </h3>
        <p 
          id="tour-description"
          className="text-sm text-muted-foreground leading-relaxed"
        >
          {currentStepData.message}
        </p>
      </div>

      {/* Step dots */}
      <div className="flex justify-center gap-1.5 pb-3">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-colors ${
              i === currentStep 
                ? 'bg-tenacity-cyan' 
                : 'bg-muted-foreground/30'
            }`}
          />
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between px-4 pb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={prevStep}
          disabled={isFirstStep}
          className="text-sm"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>

        {isLastStep ? (
          <Button
            size="sm"
            onClick={finishTour}
            className="bg-tenacity-cyan text-primary-foreground hover:bg-tenacity-cyan/90"
          >
            Finish
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={nextStep}
            className="bg-tenacity-cyan text-primary-foreground hover:bg-tenacity-cyan/90"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Don't show again option */}
      <div className="border-t border-border px-4 py-3">
        <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
          <Checkbox 
            id="dismiss-tour"
            onCheckedChange={(checked) => {
              if (checked) {
                dismissGuide();
              }
            }}
          />
          <span>Don't show this guide again</span>
        </label>
      </div>
    </motion.div>
  );
}
